/**
 * QR symbol generation and vector/raster output.
 *
 * Wraps node-qrcode's encoder only for the module matrix; drawing is done here so every output
 * (screen SVG, print SVG in millimetres, printer-resolution canvas) uses whole modules, a 4-module
 * quiet zone and pure black on white.
 */

const libQRCode = require('qrcode');

const QUIET_ZONE_MODULES = 4;

/**
 * @typedef {Object} QRMatrix
 * @property {number} Size - Modules per side, without the quiet zone.
 * @property {number} Version
 * @property {string} ErrorCorrection - L, M, Q or H.
 * @property {Array<string>} Modes - Encoding modes used (Numeric, Alphanumeric, Byte…).
 * @property {number} Length - Payload length in characters.
 * @property {(pRow: number, pColumn: number) => boolean} isDark
 */

/**
 * @param {string} pPayload
 * @param {Object} [pOptions]
 * @param {'L'|'M'|'Q'|'H'} [pOptions.ErrorCorrection] - Defaults to Q.
 * @return {QRMatrix}
 */
function createMatrix(pPayload, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpLevel = tmpOptions.ErrorCorrection || 'Q';
	const tmpSymbol = libQRCode.create(String(pPayload), { errorCorrectionLevel: tmpLevel });
	const tmpModules = tmpSymbol.modules;
	return {
		Size: tmpModules.size,
		Version: tmpSymbol.version,
		ErrorCorrection: tmpLevel,
		Modes: Array.from(new Set(tmpSymbol.segments.map((pSegment) => pSegment.mode.id))),
		Length: String(pPayload).length,
		isDark: (pRow, pColumn) => !!tmpModules.get(pRow, pColumn),
	};
}

/**
 * Build one SVG path covering all dark modules (one `h` run per horizontal stretch).
 *
 * @param {QRMatrix} pMatrix
 * @param {number} pOffset - Quiet zone in modules.
 * @return {string}
 */
function buildPath(pMatrix, pOffset)
{
	let tmpPath = '';
	for (let tmpRow = 0; tmpRow < pMatrix.Size; tmpRow++)
	{
		let tmpColumn = 0;
		while (tmpColumn < pMatrix.Size)
		{
			if (!pMatrix.isDark(tmpRow, tmpColumn))
			{
				tmpColumn++;
				continue;
			}
			const tmpStart = tmpColumn;
			while (tmpColumn < pMatrix.Size && pMatrix.isDark(tmpRow, tmpColumn))
			{
				tmpColumn++;
			}
			tmpPath += `M${tmpStart + pOffset} ${tmpRow + pOffset}h${tmpColumn - tmpStart}v1h-${tmpColumn - tmpStart}z`;
		}
	}
	return tmpPath;
}

/**
 * @param {QRMatrix} pMatrix
 * @param {Object} [pOptions]
 * @param {string} [pOptions.Width] - CSS length for width and height, e.g. `24.6mm` or `160px`. Defaults to the module count in px.
 * @param {string} [pOptions.Title] - Accessible title.
 * @param {number} [pOptions.QuietZone] - Modules; defaults to 4.
 * @return {string} an `<svg>` element
 */
function toSVG(pMatrix, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpQuietZone = (typeof tmpOptions.QuietZone === 'number') ? tmpOptions.QuietZone : QUIET_ZONE_MODULES;
	const tmpTotal = pMatrix.Size + (2 * tmpQuietZone);
	const tmpWidth = tmpOptions.Width || `${tmpTotal}px`;
	const tmpTitle = tmpOptions.Title ? `<title>${escapeXML(tmpOptions.Title)}</title>` : '';
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${tmpTotal} ${tmpTotal}" width="${tmpWidth}" height="${tmpWidth}" shape-rendering="crispEdges" role="img">${tmpTitle}<rect width="${tmpTotal}" height="${tmpTotal}" fill="#fff"/><path d="${buildPath(pMatrix, tmpQuietZone)}" fill="#000"/></svg>`;
}

/**
 * Draw the symbol (with quiet zone) onto a canvas context at whole pixels per module.
 *
 * @param {CanvasRenderingContext2D} pContext
 * @param {QRMatrix} pMatrix
 * @param {number} pX
 * @param {number} pY
 * @param {number} pPixelsPerModule - Integer.
 */
function drawToCanvas(pContext, pMatrix, pX, pY, pPixelsPerModule)
{
	const tmpScale = Math.max(1, Math.floor(pPixelsPerModule));
	const tmpTotal = (pMatrix.Size + (2 * QUIET_ZONE_MODULES)) * tmpScale;
	pContext.imageSmoothingEnabled = false;
	pContext.fillStyle = '#fff';
	pContext.fillRect(pX, pY, tmpTotal, tmpTotal);
	pContext.fillStyle = '#000';
	for (let tmpRow = 0; tmpRow < pMatrix.Size; tmpRow++)
	{
		for (let tmpColumn = 0; tmpColumn < pMatrix.Size; tmpColumn++)
		{
			if (pMatrix.isDark(tmpRow, tmpColumn))
			{
				pContext.fillRect(pX + ((tmpColumn + QUIET_ZONE_MODULES) * tmpScale), pY + ((tmpRow + QUIET_ZONE_MODULES) * tmpScale), tmpScale, tmpScale);
			}
		}
	}
}

/**
 * @param {string} pText
 * @return {string}
 */
function escapeXML(pText)
{
	return String(pText).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

module.exports =
{
	QUIET_ZONE_MODULES,
	createMatrix,
	toSVG,
	drawToCanvas,
	escapeXML,
};
